import React, { useState, useEffect } from 'react';
import { Users, Target, Trophy, TrendingUp, Calendar, ChevronRight, Share2, X, Flame, Award, RefreshCw, Crown, Medal } from 'lucide-react';
import { UserProfile } from '../types';
import { API_URL } from '../services/apiConfig';

interface LeaderboardUser {
  rank: number;
  user_id: string;
  name: string;
  picture?: string;
  total_count: number;
  badge?: string;
}

interface CommunityStats {
  totalGlobalCount: number;
  totalSadhaks: number;
  todayCount: number;
  weeklyChallenge: WeeklyChallenge;
  monthlyChallenge: MonthlyChallenge;
  groupGoal: GroupGoal;
}

interface WeeklyChallenge {
  id: string;
  title: string;
  titleHi: string;
  description: string;
  descriptionHi: string;
  targetCount: number;
  currentCount: number;
  startDate: string;
  endDate: string;
  participants: number;
  userContribution: number;
  reward: string;
}

interface MonthlyChallenge {
  id: string;
  title: string;
  titleHi: string;
  description: string;
  descriptionHi: string;
  targetCount: number;
  currentCount: number;
  month: string;
  participants: number;
  userContribution: number;
}

interface GroupGoal {
  id: string;
  title: string;
  titleHi: string;
  targetCount: number;
  currentCount: number;
  startDate: string;
  contributors: number;
  milestones: { count: number; reached: boolean; label: string }[];
}

const COMMUNITY_STATS_KEY = 'ram_naam_community_stats';
const USER_CONTRIBUTION_KEY = 'ram_naam_user_contribution';

// Get real stats from backend data
const getRealCommunityStats = (communityData: { total_jaap: number; total_users: number }): CommunityStats => {
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  
  const totalJaap = communityData.total_jaap || 0;
  const totalUsers = communityData.total_users || 0;
  
  return {
    totalGlobalCount: totalJaap,
    totalSadhaks: totalUsers,
    todayCount: Math.floor(totalJaap * 0.05), // Estimate ~5% today
    weeklyChallenge: {
      id: `week_${now.getFullYear()}_${Math.ceil((now.getDate()) / 7)}`,
      title: 'Weekly Ram Naam Challenge',
      titleHi: 'साप्ताहिक राम नाम चुनौती',
      description: 'Together complete 1 Lakh Ram Naam this week',
      descriptionHi: 'इस सप्ताह मिलकर 1 लाख राम नाम पूर्ण करें',
      targetCount: 100000,
      currentCount: Math.min(totalJaap, 100000), // Use real count capped at target
      startDate: weekStart.toISOString(),
      endDate: weekEnd.toISOString(),
      participants: totalUsers,
      userContribution: 0,
      reward: '🏆 साप्ताहिक वीर बैज'
    },
    monthlyChallenge: {
      id: `month_${now.getFullYear()}_${now.getMonth() + 1}`,
      title: 'Monthly Mega Challenge',
      titleHi: 'मासिक महा चुनौती',
      description: 'Achieve 10 Lakh Ram Naam this month',
      descriptionHi: 'इस माह 10 लाख राम नाम प्राप्त करें',
      targetCount: 1000000,
      currentCount: totalJaap,
      month: now.toLocaleString('hi-IN', { month: 'long', year: 'numeric' }),
      participants: totalUsers,
      userContribution: 0
    },
    groupGoal: {
      id: 'goal_1crore',
      title: 'One Crore Ram Naam',
      titleHi: '1 करोड़ राम नाम',
      targetCount: 10000000,
      currentCount: totalJaap,
      startDate: '2024-01-01',
      contributors: totalUsers,
      milestones: [
        { count: 1000, reached: totalJaap >= 1000, label: '1 हज़ार' },
        { count: 10000, reached: totalJaap >= 10000, label: '10 हज़ार' },
        { count: 100000, reached: totalJaap >= 100000, label: '1 लाख' },
        { count: 1000000, reached: totalJaap >= 1000000, label: '10 लाख' },
        { count: 10000000, reached: totalJaap >= 10000000, label: '1 करोड़' },
      ]
    }
  };
};

export const CommunityLeaderboard: React.FC<{ user: UserProfile; isOpen: boolean; onClose: () => void }> = ({ 
  user, 
  isOpen, 
  onClose 
}) => {
  const [stats, setStats] = useState<CommunityStats | null>(null);
  const [activeTab, setActiveTab] = useState<'leaderboard' | 'overview' | 'weekly' | 'goal'>('leaderboard');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [leaderboard, setLeaderboard] = useState<LeaderboardUser[]>([]);
  const [myRank, setMyRank] = useState<number | null>(null);
  const [communityTotal, setCommunityTotal] = useState({ total_jaap: 0, total_users: 0 });
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    if (isOpen) {
      fetchLeaderboard();
    }
  }, [isOpen, user]);
  
  const fetchLeaderboard = async () => {
    setIsLoading(true);
    try {
          const sessionToken = localStorage.getItem('session_token');
      
      // Fetch leaderboard with real data
      const response = await fetch(`${API_URL}/api/leaderboard?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard || []);
        const community = data.community || { total_jaap: 0, total_users: 0 };
        setCommunityTotal(community);
        
        // Update stats with real data
        setStats(getRealCommunityStats(community));
      }
      
      // Fetch my rank
      if (sessionToken) {
        const rankResponse = await fetch(`${API_URL}/api/leaderboard/my-rank`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` },
          credentials: 'include'
        });
        if (rankResponse.ok) {
          const rankData = await rankResponse.json();
          setMyRank(rankData.rank);
        }
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      // Use empty stats on error
      setStats(getRealCommunityStats({ total_jaap: 0, total_users: 0 }));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRefresh = () => {
    setIsRefreshing(true);
    // Refresh leaderboard from backend
    fetchLeaderboard().finally(() => {
      setIsRefreshing(false);
    });
  };
  
  const handleShare = async () => {
    if (!stats) return;
    
    const shareText = `🙏 गोलोक दिव्य नाम कोष\n\n` +
      `🎯 सामूहिक लक्ष्य: ${stats.groupGoal.targetCount.toLocaleString()} राम नाम\n` +
      `✅ पूर्ण: ${stats.groupGoal.currentCount.toLocaleString()}\n` +
      `👥 ${stats.totalSadhaks.toLocaleString()} साधक जुड़े हुए हैं\n\n` +
      `आप भी इस दिव्य अभियान में शामिल हों! 🙏`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'गोलोक दिव्य नाम कोष', text: shareText });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Copied to clipboard!');
    }
  };
  
  if (!isOpen) return null;
  
  if (isLoading || !stats) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white dark:bg-stone-900 p-8 rounded-3xl flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-saffron border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }
  
  const weekProgress = (stats.weeklyChallenge.currentCount / stats.weeklyChallenge.targetCount) * 100;
  const monthProgress = (stats.monthlyChallenge.currentCount / stats.monthlyChallenge.targetCount) * 100;
  const goalProgress = (stats.groupGoal.currentCount / stats.groupGoal.targetCount) * 100;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="community-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-lg max-h-[90vh] bg-white dark:bg-stone-900 rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-5 text-center relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20">
            <X size={20} className="text-white" />
          </button>
          <button 
            onClick={handleRefresh} 
            className={`absolute top-4 left-4 p-2 rounded-full hover:bg-white/20 ${isRefreshing ? 'animate-spin' : ''}`}
          >
            <RefreshCw size={20} className="text-white" />
          </button>
          
          <Users className="mx-auto mb-2 text-white" size={32} />
          <h2 className="text-xl font-bold text-white">सामूहिक साधना</h2>
          <p className="text-white/80 text-sm">Community Challenges</p>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-stone-700">
          {[
            { id: 'leaderboard', label: 'रैंकिंग', icon: <Trophy size={16} /> },
            { id: 'overview', label: 'सारांश', icon: <TrendingUp size={16} /> },
            { id: 'weekly', label: 'साप्ताहिक', icon: <Calendar size={16} /> },
            { id: 'goal', label: '1 करोड़', icon: <Target size={16} /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-1 transition-colors ${
                activeTab === tab.id
                  ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'leaderboard' && (
            <div className="space-y-4">
              {/* My Rank Card */}
              {myRank && (
                <div className="bg-gradient-to-r from-saffron/10 to-amber-100 dark:from-saffron/20 dark:to-amber-900/20 rounded-2xl p-4 border-2 border-saffron/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-saffron/20 flex items-center justify-center">
                        {(user as any).profilePic || user.avatar ? (
                          <img src={(user as any).profilePic || user.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="text-xl font-bold text-saffron">{user.displayName?.[0] || 'आ'}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">आपकी रैंक</p>
                        <p className="text-2xl font-bold text-saffron">#{myRank}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">कुल जाप</p>
                      <p className="text-xl font-bold text-gray-800 dark:text-white">
                        {(user.total_count || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Community Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {communityTotal.total_users.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">साधक</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {communityTotal.total_jaap.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">कुल जाप</p>
                </div>
              </div>
              
              {/* Top Rankers Header */}
              <div className="flex items-center gap-2 pt-2">
                <Crown size={18} className="text-amber-500" />
                <h3 className="font-bold text-gray-800 dark:text-white">टॉप साधक</h3>
              </div>
              
              {/* Leaderboard List */}
              <div className="space-y-2">
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Trophy size={40} className="mx-auto mb-2 opacity-50" />
                    <p>अभी कोई डेटा नहीं है</p>
                    <p className="text-sm">जाप करें और लीडरबोर्ड में आएं!</p>
                  </div>
                ) : (
                  leaderboard.map((entry, index) => (
                    <div 
                      key={entry.user_id}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                        entry.user_id === user.id 
                          ? 'bg-saffron/10 border-2 border-saffron/30' 
                          : index < 3 
                            ? 'bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10'
                            : 'bg-white dark:bg-stone-800 border border-gray-100 dark:border-stone-700'
                      }`}
                    >
                      {/* Rank */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-amber-400 text-amber-900' :
                        index === 1 ? 'bg-gray-300 text-gray-700' :
                        index === 2 ? 'bg-amber-600 text-amber-100' :
                        'bg-gray-100 dark:bg-stone-700 text-gray-600 dark:text-gray-300'
                      }`}>
                        {entry.badge || entry.rank}
                      </div>
                      
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-stone-700 flex items-center justify-center overflow-hidden">
                        {entry.picture ? (
                          <img src={entry.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg font-bold text-gray-400">{entry.name[0]}</span>
                        )}
                      </div>
                      
                      {/* Name & Count */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 dark:text-white truncate">
                          {entry.name}
                          {entry.user_id === user.id && <span className="text-saffron ml-1">(आप)</span>}
                        </p>
                        <p className="text-xs text-gray-500">
                          {entry.total_count.toLocaleString()} जाप
                        </p>
                      </div>
                      
                      {/* Badge for top 3 */}
                      {index < 3 && (
                        <Medal size={20} className={
                          index === 0 ? 'text-amber-500' :
                          index === 1 ? 'text-gray-400' :
                          'text-amber-700'
                        } />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {/* Global Stats */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-4">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">कुल सामूहिक जाप</h3>
                <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                  {stats.totalGlobalCount.toLocaleString()}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  राम नाम • {stats.totalSadhaks.toLocaleString()} साधकों द्वारा
                </p>
                <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800/30">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    आज: <span className="font-semibold text-green-600">+{stats.todayCount.toLocaleString()}</span>
                  </p>
                </div>
              </div>
              
              {/* Quick Challenge Cards */}
              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => setActiveTab('weekly')}
                  className="bg-white dark:bg-stone-800 rounded-xl p-3 border border-gray-100 dark:border-stone-700 text-left hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Flame size={16} className="text-orange-500" />
                    <span className="text-xs text-gray-500">साप्ताहिक</span>
                  </div>
                  <p className="font-bold text-gray-800 dark:text-white">
                    {Math.round(weekProgress)}%
                  </p>
                  <div className="h-1.5 bg-gray-100 dark:bg-stone-700 rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full" style={{ width: `${Math.min(weekProgress, 100)}%` }} />
                  </div>
                </button>
                
                <button 
                  onClick={() => setActiveTab('monthly')}
                  className="bg-white dark:bg-stone-800 rounded-xl p-3 border border-gray-100 dark:border-stone-700 text-left hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy size={16} className="text-purple-500" />
                    <span className="text-xs text-gray-500">मासिक</span>
                  </div>
                  <p className="font-bold text-gray-800 dark:text-white">
                    {Math.round(monthProgress)}%
                  </p>
                  <div className="h-1.5 bg-gray-100 dark:bg-stone-700 rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.min(monthProgress, 100)}%` }} />
                  </div>
                </button>
              </div>
              
              {/* Share Button */}
              <button
                onClick={handleShare}
                className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl flex items-center justify-center gap-2 font-medium"
              >
                <Share2 size={18} /> अभियान शेयर करें
              </button>
            </div>
          )}
          
          {activeTab === 'weekly' && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Flame size={24} className="text-orange-500" />
                  <div>
                    <h3 className="font-bold text-gray-800 dark:text-white">{stats.weeklyChallenge.titleHi}</h3>
                    <p className="text-xs text-gray-500">{stats.weeklyChallenge.title}</p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  {stats.weeklyChallenge.descriptionHi}
                </p>
                
                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-300">प्रगति</span>
                    <span className="font-medium text-orange-600 dark:text-orange-400">
                      {stats.weeklyChallenge.currentCount.toLocaleString()} / {stats.weeklyChallenge.targetCount.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-3 bg-white dark:bg-stone-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full transition-all"
                      style={{ width: `${Math.min(weekProgress, 100)}%` }}
                    />
                  </div>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-white/50 dark:bg-stone-800/50 rounded-lg p-2">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">
                      {stats.weeklyChallenge.participants}
                    </p>
                    <p className="text-xs text-gray-500">प्रतिभागी</p>
                  </div>
                  <div className="bg-white/50 dark:bg-stone-800/50 rounded-lg p-2">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">
                      {stats.weeklyChallenge.userContribution.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">आपका योगदान</p>
                  </div>
                </div>
                
                {/* Reward */}
                <div className="mt-4 pt-3 border-t border-orange-200 dark:border-orange-800/30">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    पुरस्कार: <span className="font-medium">{stats.weeklyChallenge.reward}</span>
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'monthly' && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy size={24} className="text-purple-500" />
                  <div>
                    <h3 className="font-bold text-gray-800 dark:text-white">{stats.monthlyChallenge.titleHi}</h3>
                    <p className="text-xs text-gray-500">{stats.monthlyChallenge.month}</p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  {stats.monthlyChallenge.descriptionHi}
                </p>
                
                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-300">प्रगति</span>
                    <span className="font-medium text-purple-600 dark:text-purple-400">
                      {stats.monthlyChallenge.currentCount.toLocaleString()} / {stats.monthlyChallenge.targetCount.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-3 bg-white dark:bg-stone-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-400 to-indigo-500 rounded-full transition-all"
                      style={{ width: `${Math.min(monthProgress, 100)}%` }}
                    />
                  </div>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-white/50 dark:bg-stone-800/50 rounded-lg p-2">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">
                      {stats.monthlyChallenge.participants}
                    </p>
                    <p className="text-xs text-gray-500">प्रतिभागी</p>
                  </div>
                  <div className="bg-white/50 dark:bg-stone-800/50 rounded-lg p-2">
                    <p className="text-xl font-bold text-gray-800 dark:text-white">
                      {stats.monthlyChallenge.userContribution.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">आपका योगदान</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'goal' && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-5">
                <div className="text-center mb-4">
                  <Target size={40} className="mx-auto text-emerald-500 mb-2" />
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-white">{stats.groupGoal.titleHi}</h3>
                  <p className="text-sm text-gray-500">सामूहिक लक्ष्य</p>
                </div>
                
                {/* Big Number */}
                <div className="text-center mb-4">
                  <p className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">
                    {stats.groupGoal.currentCount.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">
                    / {stats.groupGoal.targetCount.toLocaleString()} राम नाम
                  </p>
                </div>
                
                {/* Progress */}
                <div className="h-4 bg-white dark:bg-stone-700 rounded-full overflow-hidden mb-4">
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all"
                    style={{ width: `${Math.min(goalProgress, 100)}%` }}
                  />
                </div>
                
                {/* Milestones */}
                <div className="space-y-2 mb-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">मील के पत्थर</p>
                  {stats.groupGoal.milestones.map((milestone, index) => (
                    <div 
                      key={index}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        milestone.reached 
                          ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                          : 'bg-gray-100 dark:bg-stone-800'
                      }`}
                    >
                      <span className={`text-sm ${milestone.reached ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-500'}`}>
                        {milestone.label}
                      </span>
                      {milestone.reached ? (
                        <span className="text-emerald-500">✓</span>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {((stats.groupGoal.currentCount / milestone.count) * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Contributors */}
                <div className="text-center pt-3 border-t border-emerald-200 dark:border-emerald-800/30">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">{stats.groupGoal.contributors.toLocaleString()}</span> साधक इस अभियान में
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleShare}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl flex items-center justify-center gap-2 font-medium"
              >
                <Share2 size={18} /> दूसरों को जोड़ें
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Utility function to update user's contribution to challenges
export const updateCommunityContribution = (userId: string, count: number) => {
  const communityStats = JSON.parse(localStorage.getItem(COMMUNITY_STATS_KEY) || 'null');
  if (!communityStats) return;
  
  // Update global count
  communityStats.totalGlobalCount += count;
  communityStats.todayCount += count;
  communityStats.weeklyChallenge.currentCount += count;
  communityStats.monthlyChallenge.currentCount += count;
  communityStats.groupGoal.currentCount += count;
  
  localStorage.setItem(COMMUNITY_STATS_KEY, JSON.stringify(communityStats));
  
  // Update user's contribution
  const userContributionKey = `${USER_CONTRIBUTION_KEY}_${userId}`;
  const userContribution = JSON.parse(localStorage.getItem(userContributionKey) || '{}');
  
  userContribution.weeklyId = communityStats.weeklyChallenge.id;
  userContribution.weekly = (userContribution.weekly || 0) + count;
  userContribution.monthlyId = communityStats.monthlyChallenge.id;
  userContribution.monthly = (userContribution.monthly || 0) + count;
  userContribution.total = (userContribution.total || 0) + count;
  
  localStorage.setItem(userContributionKey, JSON.stringify(userContribution));
};

// Mini card for dashboard
export const CommunityCard: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [globalCount, setGlobalCount] = useState(0);
  
  useEffect(() => {
    const stats = JSON.parse(localStorage.getItem(COMMUNITY_STATS_KEY) || 'null');
    if (stats) {
      setGlobalCount(stats.totalGlobalCount);
    } else {
      setGlobalCount(10847523); // Default
    }
  }, []);
  
  return (
    <button
      onClick={onClick}
      className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-4 w-full text-left hover:shadow-md transition-shadow"
      data-testid="community-card"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
            <Users size={20} className="text-indigo-500" />
          </div>
          <div>
            <p className="font-semibold text-gray-800 dark:text-gray-200">सामूहिक साधना</p>
            <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
              {globalCount.toLocaleString()}
            </p>
          </div>
        </div>
        <ChevronRight size={20} className="text-gray-400" />
      </div>
    </button>
  );
};

export default CommunityLeaderboard;
